import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Emergency } from '@/models/Emergency/Emergency';
import { EmergencyMessage } from '@/models/Emergency/EmergencyMessage';

export const EmergencyChatContext = createContext();

export const EmergencyChatProvider = ({ children }) => {
  /**
   * @type {[{ emergency: Emergency | null; messages: EmergencyMessage[]; }, React.Dispatch<React.SetStateAction<{ emergency: Emergency | null; messages: EmergencyMessage[]; }>>]}
   * @description This state holds the currently selected emergency and its associated messages.
   */
  const [selectedEmergency, setSelectedEmergency] = useState(null);

  const [selectedEmergencyMessages, setSelectedEmergencyMessages] = useState(
    [],
  );

  useEffect(() => {
    if (selectedEmergency) {
      setSelectedEmergencyMessages(selectedEmergency.messages);
    } else {
      setSelectedEmergencyMessages([]);
    }
  }, [selectedEmergency]);

  const addMessageToSelectedEmergency = (message) => {
    setSelectedEmergency((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: [...prev.messages, message],
      };
    });
  };

  return (
    <EmergencyChatContext.Provider
      value={{
        setSelectedEmergencyMessages,
        selectedEmergencyMessages,
        selectedEmergency,
        setSelectedEmergency,
        addMessageToSelectedEmergency,
      }}
    >
      {children}
    </EmergencyChatContext.Provider>
  );
};

/**
 * @typedef {Object} EmergencyChatContextType
 * @property { EmergencyMessage[] } selectedEmergencyMessages - The messages of the currently selected emergency
 * @property {React.Dispatch<React.SetStateAction<EmergencyMessage[]>>} setSelectedEmergencyMessages - Function to set the messages of the selected emergency
 * @property {{
 *    emergency: Emergency | null;
 *    messages: EmergencyMessage[];
 *   }} selectedEmergency - The currently selected emergency
 * @property {React.Dispatch<React.SetStateAction<{ emergency: Emergency | null; messages: EmergencyMessage[]; locations: import('@/models/User/UserLocation').UserLocation[]} | undefined | null>>} setSelectedEmergency - Function to set the selected emergency
 * @property {(message: EmergencyMessage) => null} addMessageToSelectedEmergency - Function to add a message to the selected emergency
 */

/**
 * Custom hook to use the EmergencyChatContext
 * @returns {EmergencyChatContextType} Context values
 */
export const useEmergencyChatProvider = () => {
  return useContext(EmergencyChatContext);
};
